import { useTheme } from "@/hooks/useTheme";

// Editorial medical illustration & glowing new-age background art inspired by ivo.ai.
// GPU-accelerated drifting ambient orbs and luminous gradient line illustrations.
export function BackgroundArt() {
  const { theme } = useTheme();
  const isNight = theme === "night";

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden select-none"
      data-testid="background-art"
    >
      {/* Portability styles for floating gradients & glow effects */}
      <style>{`
        @keyframes float-slow-1 {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          50% { transform: translate(30px, -40px) scale(1.1); }
        }
        @keyframes float-slow-2 {
          0%, 100% { transform: translate(0px, 0px) scale(1.05); }
          50% { transform: translate(-35px, 25px) scale(0.95); }
        }
        @keyframes float-slow-3 {
          0%, 100% { transform: translate(0px, 0px) scale(0.98); }
          50% { transform: translate(25px, 20px) scale(1.08); }
        }
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
        @keyframes drip-flow {
          0% { transform: translateY(-10px); opacity: 0; }
          30% { opacity: 0.8; }
          100% { transform: translateY(20px); opacity: 0; }
        }
        .animate-float-1 { animation: float-slow-1 20s ease-in-out infinite; }
        .animate-float-2 { animation: float-slow-2 24s ease-in-out infinite; }
        .animate-float-3 { animation: float-slow-3 22s ease-in-out infinite; }
        .animate-heartbeat { transform-origin: 100px 100px; animation: heartbeat 3.5s ease-in-out infinite; }
        .animate-drip-1 { animation: drip-flow 2.8s ease-in-out infinite; }
        .animate-drip-2 { animation: drip-flow 2.8s ease-in-out infinite 0.9s; }
        .animate-drip-3 { animation: drip-flow 2.8s ease-in-out infinite 1.8s; }
      `}</style>

      {/* Dynamic Glowing Ambient Orbs (ivo.ai style) */}
      <div className="absolute inset-0 z-0 opacity-[0.85] dark:opacity-[0.75]">
        {/* Orb 1: Warm Sunset Sunset Rose / Peach - Top Right */}
        <div 
          className={`absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full blur-[100px] animate-float-1 transition-colors duration-500 ${
            isNight ? "bg-pink-500/15" : "bg-rose-200/35"
          }`} 
        />

        {/* Orb 2: Electric Cyan / Bright Teal (Logo Anchor) - Center Left */}
        <div 
          className={`absolute top-[25%] -left-28 w-[320px] h-[320px] rounded-full blur-[90px] animate-float-2 transition-colors duration-500 ${
            isNight ? "bg-cyan-500/18" : "bg-teal-200/40"
          }`} 
        />

        {/* Orb 3: Deep Royal Indigo / Violet (New Age accent) - Bottom Right */}
        <div 
          className={`absolute -bottom-24 -right-12 w-[400px] h-[400px] rounded-full blur-[110px] animate-float-3 transition-colors duration-500 ${
            isNight ? "bg-indigo-500/18" : "bg-indigo-200/35"
          }`} 
        />
        
        {/* Extra Orb for added depth in Night Shift */}
        {isNight && (
          <div className="absolute top-[60%] left-[20%] w-[280px] h-[280px] rounded-full blur-[100px] bg-purple-600/10 animate-pulse-slow" />
        )}
      </div>

      {/* SVG Definitions for Gradients & Glow Filters */}
      <svg className="absolute w-0 h-0" aria-hidden="true">
        <defs>
          <filter id="glow-subtle" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          
          <linearGradient id="grad-teal-indigo" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#36CDC5" />
            <stop offset="50%" stopColor="#2AA7A0" />
            <stop offset="100%" stopColor="#6366F1" />
          </linearGradient>

          <linearGradient id="grad-rose-amber" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#EC4899" />
            <stop offset="60%" stopColor="#F43F5E" />
            <stop offset="100%" stopColor="#F59E0B" />
          </linearGradient>

          <linearGradient id="grad-royal-teal" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4F46E5" />
            <stop offset="100%" stopColor="#2AA7A0" />
          </linearGradient>

          <linearGradient id="grad-navy-teal" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0A3D5F" />
            <stop offset="100%" stopColor="#2AA7A0" />
          </linearGradient>
        </defs>
      </svg>

      {/* ECG Rhythm Strip across the middle — Vivid glowing teal-to-indigo */}
      <svg
        className="absolute left-0 right-0 top-[35%] w-[220%] opacity-[0.24] dark:opacity-[0.32] -translate-x-[10%]"
        viewBox="0 0 1200 120"
        fill="none"
        stroke="url(#grad-teal-indigo)"
        strokeWidth="2.0"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M0 60 L120 60 L140 60 L160 55 L180 65 L200 30 L215 90 L230 45 L260 60 L360 60 L380 60 L400 55 L420 65 L440 30 L455 90 L470 45 L500 60 L600 60 L620 55 L640 65 L660 30 L675 90 L690 45 L720 60 L820 60 L840 55 L860 65 L880 30 L895 90 L910 45 L940 60 L1200 60" />
      </svg>

      {/* Anatomical Heart — Top Right, warm crimson/rose-to-amber line-art with gentle heartbeat animation */}
      <svg
        className="absolute -top-10 -right-10 w-[340px] h-[340px] opacity-[0.28] dark:opacity-[0.36] animate-heartbeat"
        viewBox="0 0 200 200"
        fill="none"
        stroke="url(#grad-rose-amber)"
        strokeWidth="1.6"
      >
        <path d="M100 170 C 40 130, 15 90, 40 55 C 60 30, 90 40, 100 65 C 110 40, 140 30, 160 55 C 185 90, 160 130, 100 170 Z" />
        <path d="M100 65 L100 40 M100 40 C 95 25, 80 20, 75 30 M100 40 C 105 25, 120 20, 125 30" />
        <path d="M60 90 C 70 100, 85 95, 90 110" />
        <path d="M140 90 C 130 100, 115 95, 110 110" />
        <path d="M85 130 C 95 140, 105 140, 115 130" />
      </svg>

      {/* IV Drip on the left — Navy-to-Teal stem, cascading flowing drops */}
      <svg
        className="absolute top-[8%] -left-6 w-[200px] h-[500px] opacity-[0.30] dark:opacity-[0.40]"
        viewBox="0 0 100 240"
        fill="none"
        strokeWidth="1.5"
      >
        <path
          d="M40 0 L60 0 L60 12 L70 12 L70 60 Q 50 100 50 130 Q 50 150 40 150 L 40 165"
          stroke="url(#grad-navy-teal)"
        />
        <line x1="40" y1="30" x2="40" y2="55" strokeDasharray="2 3" stroke="url(#grad-navy-teal)" />
        <line x1="40" y1="60" x2="40" y2="85" strokeDasharray="2 3" stroke="url(#grad-navy-teal)" />
        
        {/* Animated fluid drops */}
        <circle cx="40" cy="175" r="3" className="animate-drip-1" stroke="#2AA7A0" fill="#2AA7A0" fillOpacity="0.6" />
        <circle cx="40" cy="187" r="3" className="animate-drip-2" stroke="#2AA7A0" fill="#2AA7A0" fillOpacity="0.6" />
        <circle cx="40" cy="199" r="3" className="animate-drip-3" stroke="#2AA7A0" fill="#2AA7A0" fillOpacity="0.6" />
        
        <path d="M40 210 L 40 240" stroke="url(#grad-navy-teal)" />
        <path d="M30 12 L80 12" stroke="url(#grad-navy-teal)" />
      </svg>

      {/* Molecule Cluster Bottom Right — Glowing amber/rose accent */}
      <svg
        className="absolute bottom-[-30px] right-[-30px] w-[280px] h-[280px] opacity-[0.32] dark:opacity-[0.40]"
        viewBox="0 0 200 200"
        fill="none"
        stroke="url(#grad-rose-amber)"
        strokeWidth="1.6"
      >
        <circle cx="60" cy="60" r="8" />
        <circle cx="120" cy="90" r="8" />
        <circle cx="80" cy="140" r="8" />
        <circle cx="160" cy="150" r="8" />
        <circle cx="40" cy="120" r="8" />
        <line x1="60" y1="60" x2="120" y2="90" />
        <line x1="120" y1="90" x2="80" y2="140" />
        <line x1="80" y1="140" x2="160" y2="150" />
        <line x1="80" y1="140" x2="40" y2="120" />
        <line x1="40" y1="120" x2="60" y2="60" />
      </svg>

      {/* Caduceus-inspired Winged Staff Bottom Left — Muted Indigo/Royal gradient */}
      <svg
        className="absolute bottom-10 left-0 w-[150px] h-[260px] opacity-[0.24] dark:opacity-[0.34]"
        viewBox="0 0 100 200"
        fill="none"
        stroke="url(#grad-royal-teal)"
        strokeWidth="1.5"
      >
        <line x1="50" y1="20" x2="50" y2="185" />
        <path d="M50 40 C 30 55, 30 75, 50 90 C 70 105, 70 125, 50 140" />
        <path d="M50 40 C 70 55, 70 75, 50 90 C 30 105, 30 125, 50 140" />
        <path d="M30 30 Q 50 10 70 30" />
        <path d="M25 35 Q 50 25 75 35" />
      </svg>

      {/* Secondary Subtle ECG in Sunset Coral, higher up */}
      <svg
        className="absolute left-0 right-0 top-[10%] w-[220%] opacity-[0.16] dark:opacity-[0.22] -translate-x-[5%]"
        viewBox="0 0 1200 60"
        fill="none"
        stroke="url(#grad-rose-amber)"
        strokeWidth="1.4"
        strokeLinecap="round"
      >
        <path d="M0 30 L200 30 L215 20 L230 45 L245 15 L260 40 L280 30 L500 30 L515 20 L530 45 L545 15 L560 40 L580 30 L800 30 L815 20 L830 45 L845 15 L860 40 L880 30 L1200 30" />
      </svg>
    </div>
  );
}
